import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { cn } from '@/core/lib/utils';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useProjectFields } from '@/features/custom-fields/public';
import type { TaskColumnId, TaskListSortBy, TaskPriority } from '../../types';
import { ColumnSelectorPanel } from './ColumnSelectorPanel';
import { BUILT_IN_COLUMN_MAP, makeCustomFieldColumn, reorderColumns } from './columnRegistry';

export type PriorityDistribution = Partial<Record<TaskPriority, number>>;

const PRIORITY_ORDER: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];
const MIN_COLUMN_WIDTH_PX = 60;

const PRIORITY_BAR_COLOR: Record<TaskPriority, string> = {
  urgent: 'bg-chart-5',
  high: 'bg-chart-3',
  medium: 'bg-chart-4',
  low: 'bg-chart-2',
};

function PriorityBar({ distribution }: { distribution: PriorityDistribution }) {
  const total = PRIORITY_ORDER.reduce((sum, p) => sum + (distribution[p] ?? 0), 0);
  if (total === 0) return null;

  return (
    <div className="flex h-1 w-full overflow-hidden rounded-full">
      {PRIORITY_ORDER.map((p) => {
        const count = distribution[p] ?? 0;
        if (count === 0) return null;
        return (
          <div
            key={p}
            className={PRIORITY_BAR_COLOR[p]}
            style={{ width: `${(count / total) * 100}%` }}
          />
        );
      })}
    </div>
  );
}

/** Thin drag handle at a header cell's right edge — resizes that column, committed on pointer up. */
function ColumnResizeHandle({
  columnId,
  currentWidth,
  onResizing,
  onCommit,
}: {
  columnId: string;
  currentWidth: number;
  onResizing: (columnId: string, width: number | null) => void;
  onCommit: (columnId: string, width: number) => void;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  function handlePointerDown(e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: e.clientX, startWidth: currentWidth };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const next = Math.max(
      MIN_COLUMN_WIDTH_PX,
      dragRef.current.startWidth + (e.clientX - dragRef.current.startX),
    );
    onResizing(columnId, next);
  }

  function handlePointerUp(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const next = Math.max(
      MIN_COLUMN_WIDTH_PX,
      dragRef.current.startWidth + (e.clientX - dragRef.current.startX),
    );
    dragRef.current = null;
    onResizing(columnId, null);
    onCommit(columnId, next);
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      data-testid={`tasks-column-resize-${columnId}`}
      className="absolute inset-y-0 right-0 w-1 shrink-0 cursor-col-resize touch-none hover:bg-primary/40"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  );
}

type DropEdge = 'left' | 'right';

/**
 * Draggable wrapper for one dynamic column header cell — reordering via drag-and-drop.
 * Shows where the dragged column will land: a vertical bar on the edge of the cell currently
 * under the pointer (left half vs. right half), and dims the cell being dragged.
 */
function DraggableColumnCell({
  columnId,
  width,
  breakpoint,
  isDragging,
  dropEdge,
  onDragStateChange,
  onReorder,
  className,
  children,
}: {
  columnId: TaskColumnId;
  width: number;
  breakpoint: string;
  isDragging: boolean;
  dropEdge: DropEdge | null;
  onDragStateChange: (
    state:
      | { draggingCol: TaskColumnId | null }
      | { dragOverCol: TaskColumnId | null; edge: DropEdge | null },
  ) => void;
  onReorder: (sourceCol: TaskColumnId, targetCol: TaskColumnId, edge: DropEdge) => void;
  className?: string;
  children: ReactNode;
}) {
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const cleanupDraggable = draggable({
      element: el,
      getInitialData: () => ({ columnId }),
      onDragStart: () => onDragStateChange({ draggingCol: columnId }),
      onDrop: () => onDragStateChange({ draggingCol: null }),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: el,
      getData: () => ({ columnId }),
      onDrag: ({ location }) => {
        const rect = el.getBoundingClientRect();
        const edge: DropEdge =
          location.current.input.clientX - rect.left < rect.width / 2 ? 'left' : 'right';
        onDragStateChange({ dragOverCol: columnId, edge });
      },
      onDragLeave: () => onDragStateChange({ dragOverCol: null, edge: null }),
      onDrop: ({ source, location }) => {
        const rect = el.getBoundingClientRect();
        const edge: DropEdge =
          location.current.input.clientX - rect.left < rect.width / 2 ? 'left' : 'right';
        onDragStateChange({ dragOverCol: null, edge: null });
        const sourceColumnId = source.data.columnId as TaskColumnId | undefined;
        if (sourceColumnId) onReorder(sourceColumnId, columnId, edge);
      },
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [columnId, onDragStateChange, onReorder]);

  return (
    <div
      ref={elRef}
      style={{ width }}
      data-testid={`tasks-column-cell-${columnId}`}
      className={cn('relative hidden shrink-0', breakpoint, isDragging && 'opacity-40', className)}
    >
      {dropEdge && (
        <div
          data-testid={`tasks-column-drop-indicator-${columnId}`}
          className={cn(
            'pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-primary',
            dropEdge === 'left' ? 'left-0' : 'right-0',
          )}
        />
      )}
      {children}
    </div>
  );
}

interface ColumnHeaderProps {
  visibleColumns: TaskColumnId[];
  columnWidths: Record<string, number>;
  sortBy?: TaskListSortBy;
  priorityDistribution?: PriorityDistribution;
  selectedCount?: number;
  totalCount?: number;
  projectId?: string;
  onSelectAll?: (checked: boolean) => void;
  onSortChange: (sortBy: TaskListSortBy | undefined) => void;
  onColumnsChange: (columns: TaskColumnId[]) => void;
  onColumnWidthsChange: (widths: Record<string, number>) => void;
}

export function ColumnHeader({
  visibleColumns,
  columnWidths,
  sortBy,
  priorityDistribution,
  selectedCount = 0,
  totalCount = 0,
  projectId,
  onSelectAll,
  onSortChange,
  onColumnsChange,
  onColumnWidthsChange,
}: ColumnHeaderProps) {
  const { t } = useTranslation('tasks');
  const [liveWidths, setLiveWidths] = useState<Record<string, number>>({});
  const [draggingCol, setDraggingCol] = useState<TaskColumnId | null>(null);
  const [dropTarget, setDropTarget] = useState<{ col: TaskColumnId; edge: DropEdge } | null>(null);

  const { data: fieldsResult, isLoading: fieldsLoading } = useProjectFields(projectId ?? '');
  const fieldMap = Object.fromEntries((fieldsResult?.items ?? []).map((f) => [f.id ?? '', f]));

  const showPriorityBar = !!priorityDistribution && visibleColumns.includes('priority');
  const dynamicCols = visibleColumns.filter((c) => c !== 'title');

  function handleSort(col: TaskColumnId) {
    if (sortBy?.columnId === col) {
      if (sortBy.direction === 'asc') {
        onSortChange({ columnId: col, direction: 'desc' });
      } else {
        onSortChange(undefined);
      }
    } else {
      onSortChange({ columnId: col, direction: 'asc' });
    }
  }

  function resolveWidth(columnId: string, defaultWidthPx: number | undefined) {
    return liveWidths[columnId] ?? columnWidths[columnId] ?? defaultWidthPx ?? MIN_COLUMN_WIDTH_PX;
  }

  function handleResizing(columnId: string, width: number | null) {
    setLiveWidths((prev) => {
      if (width === null) {
        const { [columnId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnId]: width };
    });
  }

  function handleResizeCommit(columnId: string, width: number) {
    onColumnWidthsChange({ ...columnWidths, [columnId]: width });
  }

  function handleDragStateChange(
    state:
      | { draggingCol: TaskColumnId | null }
      | { dragOverCol: TaskColumnId | null; edge: DropEdge | null },
  ) {
    if ('draggingCol' in state) {
      setDraggingCol(state.draggingCol);
      return;
    }
    setDropTarget(
      state.dragOverCol && state.edge ? { col: state.dragOverCol, edge: state.edge } : null,
    );
  }

  function handleReorder(sourceCol: TaskColumnId, targetCol: TaskColumnId, edge: DropEdge) {
    const reordered = reorderColumns(dynamicCols, sourceCol, targetCol, edge);
    onColumnsChange(['title', ...reordered]);
  }

  return (
    <div
      data-testid="tasks-listview-column-header"
      className="group/header border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground"
    >
      {/* Label row */}
      <div className="flex items-center gap-3">
        <div className="w-7 shrink-0" />

        <div className="flex w-5 shrink-0 items-center justify-center">
          <Checkbox
            data-testid="tasks-column-header-select-all"
            checked={
              totalCount > 0 && selectedCount === totalCount
                ? true
                : selectedCount > 0
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(v) => onSelectAll?.(!!v)}
            className={cn(
              'shrink-0 transition-opacity',
              selectedCount === 0 && 'opacity-0 group-hover/header:opacity-100',
            )}
            aria-label="Select all tasks"
          />
        </div>

        {/* Title column — always visible, not reorderable/resizable */}
        <button
          type="button"
          data-testid="tasks-sort-title"
          className="flex w-56 shrink-0 items-center gap-1 truncate p-3 text-left hover:text-foreground md:w-80"
          onClick={() => handleSort('title')}
        >
          {t('listView.columns.title')}
          {sortBy?.columnId === 'title' &&
            (sortBy.direction === 'asc' ? (
              <ArrowUp className="size-3 shrink-0" />
            ) : (
              <ArrowDown className="size-3 shrink-0" />
            ))}
        </button>

        {/* Dynamic columns — resolved from registry, draggable to reorder, resizable */}
        {dynamicCols.map((col) => {
          const builtIn = BUILT_IN_COLUMN_MAP[col];
          if (builtIn) {
            const label = builtIn.labelKey ? t(builtIn.labelKey) : (builtIn.label ?? col);
            const width = resolveWidth(col, builtIn.defaultWidthPx);
            return (
              <DraggableColumnCell
                key={col}
                columnId={col}
                width={width}
                breakpoint={builtIn.breakpoint}
                isDragging={draggingCol === col}
                dropEdge={dropTarget?.col === col ? dropTarget.edge : null}
                onDragStateChange={handleDragStateChange}
                onReorder={handleReorder}
              >
                <button
                  type="button"
                  data-testid={`tasks-sort-${col}`}
                  className="flex h-full w-full cursor-grab items-center gap-1 truncate p-3 text-left hover:text-foreground active:cursor-grabbing"
                  onClick={() => builtIn.sortable && handleSort(col)}
                >
                  {label}
                  {sortBy?.columnId === col &&
                    (sortBy.direction === 'asc' ? (
                      <ArrowUp className="size-3 shrink-0" />
                    ) : (
                      <ArrowDown className="size-3 shrink-0" />
                    ))}
                </button>
                <ColumnResizeHandle
                  columnId={col}
                  currentWidth={width}
                  onResizing={handleResizing}
                  onCommit={handleResizeCommit}
                />
              </DraggableColumnCell>
            );
          }

          const field = fieldMap[col];
          if (field) {
            const def = makeCustomFieldColumn(field);
            const width = resolveWidth(col, def.defaultWidthPx);
            return (
              <DraggableColumnCell
                key={col}
                columnId={col}
                width={width}
                breakpoint={def.breakpoint}
                isDragging={draggingCol === col}
                dropEdge={dropTarget?.col === col ? dropTarget.edge : null}
                onDragStateChange={handleDragStateChange}
                onReorder={handleReorder}
                className="cursor-grab items-center gap-1 p-3 active:cursor-grabbing"
              >
                {def.label ?? col}
                <ColumnResizeHandle
                  columnId={col}
                  currentWidth={width}
                  onResizing={handleResizing}
                  onCommit={handleResizeCommit}
                />
              </DraggableColumnCell>
            );
          }

          // Column ID not yet resolved (fields still loading) — show skeleton
          if (fieldsLoading) {
            return (
              <div key={col} className="hidden w-32 shrink-0 p-3 md:block">
                <Skeleton className="h-3 w-20" />
              </div>
            );
          }

          return null;
        })}

        {/* Overflow placeholder + column selector */}
        <div className="flex w-8 shrink-0 items-center justify-end">
          <ColumnSelectorPanel
            visibleColumns={visibleColumns}
            onColumnsChange={onColumnsChange}
            projectId={projectId}
          />
        </div>
      </div>

      {/* Priority distribution bar */}
      {showPriorityBar && (
        <div className="flex gap-3">
          <div className="w-7 shrink-0" />
          <div className="w-4 shrink-0" />
          <div className="w-56 shrink-0 md:w-80" />

          {dynamicCols.map((col) => {
            const builtIn = BUILT_IN_COLUMN_MAP[col];
            const width = resolveWidth(col, builtIn?.defaultWidthPx);
            const breakpoint = builtIn?.breakpoint ?? 'md:block';
            return (
              <div
                key={col}
                style={{ width }}
                className={cn('hidden shrink-0 px-3 pb-1.5', breakpoint)}
              >
                {col === 'priority' && <PriorityBar distribution={priorityDistribution!} />}
              </div>
            );
          })}

          <div className="w-8 shrink-0" />
        </div>
      )}
    </div>
  );
}
